import { isDefined } from "@ls/js-utils";
import Drawer from "@ls/react-drawer";
import AccountSelector from "components/AccountSelector";
import AnalyticsSelector from "components/AnalyticsSelector";
import BookAnalyticsSelector from "components/BookAnalyticsSelector";
import AsOfDatePicker from "components/AsOfDatePicker";
import BenchmarkSelector from "components/BenchmarkSelector";
import BetaSelector from "components/BetaSelector";
import DefaultBenchmarkSwitch from "components/DefaultBenchmarkSwitch";
import FactorAttributionSelector from "components/FactorAtrtributionSelector";
import LevelSelector from "components/LevelSelector";
import SecurityLabelSelector from "components/SecurityLabelSelector";
import LookThruSwitch from "components/LookThruSwitch";
import MinAccWeightInput from "components/MinAccWeightInput";
import MinBenchWeightInput from "components/MinBenchWeightInput";
import MiscSelector from "components/MiscSelector";
import SecurityAttributesSelector from "components/SecurityAttributesSelector";
import TraceAnalyticsSelector from "components/TraceAnalyticsSelector/TraceAnalyticsSelector";
import TrackingErrorSelector from "components/TrackingErrorSelector";
import RatingsSelector from "components/RatingsSelector";
import React from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { startRequests, toggleDrawer } from "state/app/actions";
import { selectIsDrawerOpen, selectAppNameTitle } from "state/app/selectors";
import { commitSelections } from "state/selections/actions";
import SelectionState from "state/selections/SelectionState";
import {
  selectSelectedAccountEntityId,
  selectSelectedAsOfDate,
  selectSelectedGroupingLevels,
} from "state/selections/selectors";
import { selectBucketLevels } from "state/data/selectors";
import { selectDefinitions } from "state/factorAttribution/selectors";
import ScenarioSelector from "components/ScenarioSelector/ScenarioSelector";
import BenchmarkScenarioSelector from "components/BenchmarkScenarioSelector/BenchmarkScenarioSelector";
import ScenarioAnalysisSelector from "components/ScenarioAnalysisSelector/ScenarioAnalysisSelector";
import Scan2Selector from "components/Scan2Selector/Scan2Selector";
import styled from "styled-components";
import { Cell, Grid } from "styled-css-grid";
import { useLocation } from "react-router-dom";
import { selectFetchingEntities } from "state/data/selectors";

const Root = styled.div`
  width: 800px;
  height: 100%;
  max-width: 100vw;
  position: relative;
  display: flex;
  flex-direction: row;
`;
const Item = styled((props) => <Cell center middle {...props} />)`
  display: block;
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 3px;
  align-self: center;
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 2px 3px rgba(0, 0, 0, 0.23);
  }
`;

const FitItem = styled(Item)`
  width: fit-content;
`;

const Selections = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: auto;
`;

const InnerSelectionsScroller = styled.div`
  position: absolute;
  padding: 10px 7px;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const Selection = (props) => {
  const dispatch = useDispatch();
  const appNameTitle = useSelector(selectAppNameTitle);
  const drawerOpen = useSelector(selectIsDrawerOpen);
  const accEntityId = useSelector(
    selectSelectedAccountEntityId(SelectionState.UNCOMMITTED)
  );
  const levels = useSelector(
    selectSelectedGroupingLevels(SelectionState.UNCOMMITTED)
  );
  const asOfDate = useSelector(
    selectSelectedAsOfDate(SelectionState.UNCOMMITTED)
  );
  const bucketLevels = useSelector(selectBucketLevels);
  const definitions = useSelector(
    selectDefinitions(SelectionState.UNCOMMITTED)
  );
  const fetchingEntities = useSelector(selectFetchingEntities);
  const onClickCloseButton = React.useCallback(
    () => dispatch(toggleDrawer()),
    [dispatch]
  );
  const onButtonClick = React.useCallback(() => {
    batch(() => {
      dispatch(toggleDrawer());
      dispatch(commitSelections());
      dispatch(startRequests());
    });
  }, [dispatch]);

  const { search } = useLocation();
  const hideEntitySelectors = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("hideEntitySelectors") === "true";
  }, [search]);

  const hideHamburgerDropDowns = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("hideHamburgerDropDowns") === "true";
  }, [search]);

  const goButtonProps = React.useMemo(() => {
    let disabled = false,
      tooltip = null;
    const missing = [];

    if (!isDefined(accEntityId)) {
      missing.push("Account");
      disabled = true;
    }
    if (!isDefined(levels) || levels.length === 0) {
      missing.push("Bucket levels");
      disabled = true;
    }
    if (fetchingEntities) {
      missing.push("Fetching entities");
      disabled = true;
    }
    if (isDefined(levels) && !!definitions) {
      const _levels = bucketLevels["data"]?.filter(
        (bucket) => levels.includes(bucket.id) && !bucket.fatBucket
      );
      definitions.forEach((element) => {
        const { scaleType } = element;
        if (scaleType === "WeightedAverage" && _levels?.length > 0) {
          missing.push(`Bucket levels not supported for ${scaleType}`);
          disabled = true;
        }
      });
    }
    if (!isDefined(asOfDate)) {
      missing.push("As of date");
      disabled = true;
    }

    if (missing.length) {
      tooltip = `Missing input: ${missing.join(", ")}`;
    }
    return {
      disabled,
      tooltip,
    };
  }, [
    accEntityId,
    asOfDate,
    levels,
    definitions,
    bucketLevels,
    fetchingEntities,
  ]);
  return (
    <div title={goButtonProps["tooltip"]}>
      <Drawer
        active={drawerOpen}
        onClickCloseButton={onClickCloseButton}
        unmountOnExit={false}
        title={!!appNameTitle ? appNameTitle : "Portfolio Analytics"}
        onClickGo={onButtonClick}
        goButtonProps={goButtonProps}
      >
        <Root>
          <Selections>
            <InnerSelectionsScroller>
              <Grid columns={12} gap="8px">
                <FitItem width={12}>
                  <AsOfDatePicker />
                </FitItem>
                {!hideEntitySelectors && (
                  <>
                    <Item width={12}>
                      <AccountSelector />
                    </Item>
                    <Item width={12}>
                      <DefaultBenchmarkSwitch />
                    </Item>
                    <Item width={12}>
                      <BenchmarkSelector />
                    </Item>
                    <Item width={12}>
                      <ScenarioSelector />
                    </Item>
                    <Item width={12}>
                      <BenchmarkScenarioSelector />
                    </Item>
                  </>
                )}
                <Item width={12}>
                  <LookThruSwitch />
                </Item>
                <Item width={6}>
                  <MinAccWeightInput />
                </Item>
                <Item width={6}>
                  <MinBenchWeightInput />
                </Item>
                <Cell width={12}>
                  <LevelSelector />
                </Cell>
                <Cell width={12}>
                  <SecurityLabelSelector />
                </Cell>
                {!hideHamburgerDropDowns && (
                  <>
                    <Item width={12}>
                      <AnalyticsSelector />
                    </Item>
                    <Item width={12}>
                      <BetaSelector />
                    </Item>
                    <Item width={12}>
                      <BookAnalyticsSelector />
                    </Item>
                    <Item width={12}>
                      <FactorAttributionSelector />
                    </Item>
                    <Item width={12}>
                      <RatingsSelector />
                    </Item>
                    <Item width={12}>
                      <SecurityAttributesSelector />
                    </Item>
                    <Item width={12}>
                      <TrackingErrorSelector />
                    </Item>
                    <Item width={12}>
                      <TraceAnalyticsSelector />
                    </Item>
                    {process.env.REACT_APP_HOST_ENV !== "AZURE" && (
                      <Item width={12}>
                        <MiscSelector />
                      </Item>
                    )}
                    <Item width={12}>
                      <ScenarioAnalysisSelector />
                    </Item>
                  </>
                )}
                <Item width={12}>
                  <Scan2Selector />
                </Item>
                <Item width={12}></Item>
              </Grid>
            </InnerSelectionsScroller>
          </Selections>
        </Root>
      </Drawer>
    </div>
  );
};
export default Selection;
